/**
 * Compositeur — là où le chatteur écrit (famille « conversation », 01-11-2026). Une grande pilule
 * à la surface des champs (`.ch-surface` : le focus s'y voit à la marque) : à gauche joindre et
 * ce que l'app y ajoute, le champ multiligne qui GRANDIT avec le texte (jusqu'à `hauteurMax`,
 * puis il défile), à droite l'emoji, le vocal, et le bouton rond
 * d'envoi — plein quand il y a quelque chose à envoyer, éteint sinon. Tous des `Button`.
 *
 * Joindre et l'emoji ouvrent un `Volet` ancré sur leur bouton quand l'app leur donne un contenu
 * (`joindre`, `emoji` : une fonction qui reçoit `fermer`) — le sélecteur d'emoji, le menu des
 * pièces jointes ; `onJoindre` seul fait une action directe (ouvrir le Vault).
 *
 * Au-dessus, dans l'ordre : `bandeaux` (le `BrouillonPropose` de Cyrano, une correction, une
 * erreur — ce que l'app empile), la réponse en préparation (`reponseA`, une `Citation` qu'on
 * annule), les pièces jointes. `verrouille` remplace la saisie par une `Alerte` (« Démarre ton
 * shift pour écrire », « X a la main sur ce fan »).
 *
 * Le clavier : Entrée envoie, Maj+Entrée va à la ligne (`envoiAvec="ctrl-entree"` : Ctrl/⌘+Entrée
 * envoie, Entrée va à la ligne — l'édition d'un message) ; Ctrl/⌘+Entrée envoie toujours ; Échap
 * appelle `onEchap` (écarter une suggestion, annuler une réponse). Une composition en cours
 * (japonais, coréen…) n'envoie jamais. `onKeyDown` passe AVANT : un `preventDefault()` y coupe
 * le reste (Tab qui accepte une correction).
 *
 *   <Compositeur valeur={texte} onChange={setTexte} onEnvoyer={envoyer}
 *                emoji={(fermer) => <SelecteurEmoji onChoix={(e) => { inserer(e); fermer(); }} />}
 *                joindre={(fermer) => <MenuJoindre onFini={fermer} />} bandeaux={<BrouillonPropose … />} />
 */
import { useLayoutEffect, useRef, type ClipboardEvent, type KeyboardEvent, type ReactNode, type Ref } from "react";

import { Alerte } from "../Alerte";
import { Button } from "../Button";
import { Lock, Mic, Paperclip, Send, Smile, X } from "../icons";
import { Volet, useVolet } from "../Volet";
import { cn } from "../utils";
import { Citation } from "./Citation";
import "../champs.css";
import "./conversation.css";

export interface PieceJointe {
  id: string;
  nom: ReactNode;
  /** Une miniature (`<img>`), 32 px. */
  apercu?: ReactNode;
  /** Retirer la pièce. Absent : pas de croix. */
  onRetirer?: () => void;
}

export interface LibellesCompositeur {
  champ: string;
  envoyer: string;
  joindre: string;
  emoji: string;
  vocal: string;
  retirer: string;
  annulerReponse: string;
}

export const LIBELLES_COMPOSITEUR: LibellesCompositeur = {
  champ: "Message",
  envoyer: "Envoyer",
  joindre: "Joindre un média",
  emoji: "Emoji",
  vocal: "Enregistrer un vocal",
  retirer: "Retirer",
  annulerReponse: "Ne plus répondre à ce message",
};

export interface CompositeurProps {
  valeur: string;
  onChange: (valeur: string) => void;
  /** Envoyer ce qui est écrit (et les pièces jointes). */
  onEnvoyer: () => void;
  /** Défaut « Écris ton message… ». */
  placeholder?: string;
  /** `entree` (défaut) : Entrée envoie. `ctrl-entree` : seul Ctrl/⌘+Entrée envoie. */
  envoiAvec?: "entree" | "ctrl-entree";
  /** Échap dans le champ. */
  onEchap?: () => void;
  /** Avant tout le reste ; `preventDefault()` coupe la suite. */
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Un collage (une image du presse-papiers). */
  onPaste?: (e: ClipboardEvent<HTMLTextAreaElement>) => void;
  piecesJointes?: readonly PieceJointe[];
  /** Le contenu du volet du trombone (le menu des pièces jointes). */
  joindre?: (fermer: () => void) => ReactNode;
  /** Le trombone sans volet : une action directe. */
  onJoindre?: () => void;
  /** Le contenu du volet du sourire (le sélecteur d'emoji). */
  emoji?: (fermer: () => void) => ReactNode;
  /** Le micro, à droite. */
  onVocal?: () => void;
  /** Le micro est allumé (un vocal s'enregistre). */
  vocalActif?: boolean;
  /** D'autres boutons à gauche (après le trombone). */
  avant?: ReactNode;
  /** D'autres boutons à droite (avant l'envoi) : la dictée, les scripts. */
  apres?: ReactNode;
  /** Empilés au-dessus : `BrouillonPropose`, une correction, une erreur. */
  bandeaux?: ReactNode;
  /** La réponse en préparation : le message cité, et comment l'annuler. */
  reponseA?: { auteur?: ReactNode; extrait: ReactNode; onAnnuler: () => void };
  /** L'envoi part : le bouton tourne, rien ne repart. */
  enCours?: boolean;
  desactive?: boolean;
  /** Remplace la saisie : pourquoi on ne peut pas écrire. */
  verrouille?: ReactNode;
  /** La hauteur max du champ, en px, avant qu'il ne défile. Défaut 200. */
  hauteurMax?: number;
  autoFocus?: boolean;
  refChamp?: Ref<HTMLTextAreaElement>;
  libelles?: Partial<LibellesCompositeur>;
  className?: string;
}

/** Un bouton de la saisie : 40 px (36 sur un téléphone, pour laisser la place d'écrire). */
const ICONE = "h-9 w-9 sm:h-10 sm:w-10 [&_svg]:h-5 [&_svg]:w-5 sm:[&_svg]:h-[22px] sm:[&_svg]:w-[22px]";

function assigner<T>(ref: Ref<T> | undefined, valeur: T | null) {
  if (!ref) return;
  if (typeof ref === "function") ref(valeur);
  else (ref as { current: T | null }).current = valeur;
}

export function Compositeur({
  valeur,
  onChange,
  onEnvoyer,
  placeholder = "Écris ton message…",
  envoiAvec = "entree",
  onEchap,
  onKeyDown,
  onPaste,
  piecesJointes = [],
  joindre,
  onJoindre,
  emoji,
  onVocal,
  vocalActif = false,
  avant,
  apres,
  bandeaux,
  reponseA,
  enCours = false,
  desactive = false,
  verrouille,
  hauteurMax = 200,
  autoFocus = false,
  refChamp,
  libelles,
  className,
}: CompositeurProps) {
  const l = { ...LIBELLES_COMPOSITEUR, ...libelles };
  const champ = useRef<HTMLTextAreaElement | null>(null);
  const voletJoindre = useVolet();
  const voletEmoji = useVolet();
  const envoyable = (valeur.trim().length > 0 || piecesJointes.length > 0) && !enCours && !desactive;

  // Le champ grandit avec le texte, jusqu'à `hauteurMax`, puis il défile.
  useLayoutEffect(() => {
    const el = champ.current;
    if (!el) return;
    if (!valeur) {
      // vide : une ligne, même quand le placeholder en voudrait deux
      el.style.height = "";
      el.style.overflowY = "hidden";
      return;
    }
    el.style.height = "auto";
    const voulue = el.scrollHeight;
    el.style.height = `${Math.min(voulue, hauteurMax)}px`;
    el.style.overflowY = voulue > hauteurMax ? "auto" : "hidden";
  }, [valeur, hauteurMax]);

  const envoyer = () => {
    if (envoyable) onEnvoyer();
  };

  const surClavier = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === "Escape" && onEchap) {
      e.preventDefault();
      onEchap();
      return;
    }
    if (e.key !== "Enter") return;
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl || (envoiAvec === "entree" && !e.shiftKey)) {
      e.preventDefault();
      envoyer();
    }
  };

  return (
    <div className={cn("cv-compositeur", className)} data-desactive={desactive || undefined}>
      {bandeaux}
      {reponseA && (
        <div className="cv-repondre">
          <Citation auteur={reponseA.auteur} extrait={reponseA.extrait} />
          <Button variant="ghost" size="lg" iconOnly className={ICONE} aria-label={l.annulerReponse} title={l.annulerReponse} onClick={reponseA.onAnnuler}>
            <X aria-hidden />
          </Button>
        </div>
      )}
      {piecesJointes.length > 0 && (
        <ul className="cv-pieces" aria-label={l.joindre}>
          {piecesJointes.map((p) => (
            <li key={p.id} className="cv-piece">
              {p.apercu !== undefined && <span className="cv-piece-apercu">{p.apercu}</span>}
              <span className="cv-piece-nom">{p.nom}</span>
              {p.onRetirer && (
                <Button variant="ghost" size="md" iconOnly className="h-[30px] w-[30px] [&_svg]:h-4 [&_svg]:w-4" aria-label={l.retirer} title={l.retirer} onClick={p.onRetirer}>
                  <X aria-hidden />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {verrouille !== undefined && verrouille !== null && verrouille !== false ? (
        <Alerte ton="info" icone={<Lock />} texte={verrouille} />
      ) : (
        <div className="cv-saisie ch-surface" data-eteint={desactive || undefined}>
          {(joindre || onJoindre || avant) && (
            <div className="cv-saisie-cote">
              {(joindre || onJoindre) && (
                <Button
                  ref={voletJoindre.ancre}
                  variant={voletJoindre.ouvert ? "accent" : "ghost"}
                  size="lg"
                  iconOnly
                  className={ICONE}
                  aria-label={l.joindre}
                  title={l.joindre}
                  aria-haspopup={joindre ? "dialog" : undefined}
                  aria-expanded={joindre ? voletJoindre.ouvert : undefined}
                  onClick={joindre ? voletJoindre.basculer : onJoindre}
                  disabled={desactive}
                >
                  <Paperclip aria-hidden />
                </Button>
              )}
              {avant}
            </div>
          )}
          <textarea
            ref={(el) => {
              champ.current = el;
              assigner(refChamp, el);
            }}
            className="cv-champ"
            rows={1}
            value={valeur}
            placeholder={placeholder}
            aria-label={l.champ}
            disabled={desactive}
            autoFocus={autoFocus}
            spellCheck={false}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={surClavier}
            onPaste={onPaste}
          />
          <div className="cv-saisie-cote">
            {emoji && (
              <Button
                ref={voletEmoji.ancre}
                variant={voletEmoji.ouvert ? "accent" : "ghost"}
                size="lg"
                iconOnly
                className={ICONE}
                aria-label={l.emoji}
                title={l.emoji}
                aria-haspopup="dialog"
                aria-expanded={voletEmoji.ouvert}
                onClick={voletEmoji.basculer}
                disabled={desactive}
              >
                <Smile aria-hidden />
              </Button>
            )}
            {apres}
            {onVocal && (
              <Button
                variant={vocalActif ? "accent" : "ghost"}
                size="lg"
                iconOnly
                className={ICONE}
                aria-label={l.vocal}
                aria-pressed={vocalActif}
                title={l.vocal}
                onClick={onVocal}
                disabled={desactive}
              >
                <Mic aria-hidden />
              </Button>
            )}
            <Button
              variant="primary"
              size="lg"
              iconOnly
              className="h-10 w-10 sm:h-[42px] sm:w-[42px] [&_svg]:h-5 [&_svg]:w-5"
              aria-label={l.envoyer}
              title={l.envoyer}
              onClick={envoyer}
              disabled={!envoyable && !enCours}
              enCours={enCours}
            >
              <Send aria-hidden />
            </Button>
          </div>
        </div>
      )}
      {joindre && (
        <Volet ancre={voletJoindre.ancre} ouvert={voletJoindre.ouvert} onClose={voletJoindre.fermer} ariaLabel={l.joindre}>
          {joindre(voletJoindre.fermer)}
        </Volet>
      )}
      {emoji && (
        <Volet ancre={voletEmoji.ancre} ouvert={voletEmoji.ouvert} onClose={voletEmoji.fermer} alignement="droite" ariaLabel={l.emoji}>
          {emoji(voletEmoji.fermer)}
        </Volet>
      )}
    </div>
  );
}
